import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Database } from '@/lib/supabase'
import {
  applyChatContactScopeFilter,
  contactMatchesScope,
} from '@/lib/chatContactScope'

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface UseInfiniteContactsReturn {
  contacts: AccountContact[]
  initialLoading: boolean
  loadingMore: boolean
  error: string | null
  hasMore: boolean
  loadMore: () => Promise<void>
  refetch: () => Promise<void>
  updateContact: (id: string, data: Partial<AccountContact>) => Promise<boolean>
  totalCount: number
}

const PAGE_SIZE = 50

interface UseInfiniteContactsProps {
  searchTerm?: string
}

export const useInfiniteContacts = ({ searchTerm = '' }: UseInfiniteContactsProps = {}): UseInfiniteContactsReturn => {
  const { user } = useAuth()
  const [contacts, setContacts] = useState<AccountContact[]>([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const fetchContacts = useCallback(async (currentPage: number, append: boolean = false): Promise<void> => {
    if (!user?.account_id || !supabase) {
      setInitialLoading(false)
      return
    }

    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setInitialLoading(true)
      }
      setError(null)

      const from = currentPage * PAGE_SIZE
      const to = from + PAGE_SIZE - 1

      let query = supabase
        .from('account_contacts')
        .select('*', { count: 'exact' })
        .eq('account_id', user.account_id)

      query = applyChatContactScopeFilter(query, user)

      // Se houver termo de busca, aplicar filtro
      if (searchTerm.trim()) {
        const searchPattern = `%${searchTerm.trim()}%`
        query = query.or(`name.ilike.${searchPattern},display_name.ilike.${searchPattern},push_name_last.ilike.${searchPattern},phone.ilike.${searchPattern},email.ilike.${searchPattern},jid.ilike.${searchPattern}`)
      }

      const { data: contactsData, error: contactsError, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to)

      if (contactsError) throw contactsError

      const newContacts = contactsData || []

      if (append) {
        setContacts(prev => [...prev, ...newContacts])
      } else {
        setContacts(newContacts)
      }

      setTotalCount(count || 0)
      setHasMore(newContacts.length === PAGE_SIZE && (from + newContacts.length) < (count || 0))
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar contatos')
    } finally {
      if (append) {
        setLoadingMore(false)
      } else {
        setInitialLoading(false)
      }
    }
  }, [
    user?.account_id,
    user?.auth_user_id,
    user?.view_all_contacts,
    user?.view_unassigned_contacts,
    user?.type,
    searchTerm,
  ])

  const loadMore = useCallback(async (): Promise<void> => {
    if (!hasMore || loadingMore) return

    const nextPage = page + 1
    setPage(nextPage)
    await fetchContacts(nextPage, true)
  }, [hasMore, loadingMore, page, fetchContacts])

  const refetch = useCallback(async (): Promise<void> => {
    setPage(0)
    setHasMore(true)
    await fetchContacts(0, false)
  }, [fetchContacts])

  const updateContact = async (id: string, data: Partial<AccountContact>): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)
      const { error } = await supabase
        .from('account_contacts')
        .update(data)
        .eq('id', id)
        .eq('account_id', user.account_id)

      if (error) throw error

      // Update contact in local state
      setContacts(prev =>
        prev.map(c => c.id === id ? { ...c, ...data } : c)
      )

      return true
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar contato')
      return false
    }
  }

  // Reset pagination when search term changes
  useEffect(() => {
    setPage(0)
    setHasMore(true)
    fetchContacts(0, false)
  }, [searchTerm, fetchContacts])

  useEffect(() => {
    if (!user?.account_id || !supabase) {
      return
    }

    console.log('🔄 [useInfiniteContacts] Setting up contacts subscription')

    const channel = supabase
      .channel('infinite-contacts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_contacts',
          filter: `account_id=eq.${user.account_id}`
        },
        (payload) => {
          console.log('📇 [useInfiniteContacts] Contact change detected:', payload.eventType)

          if (payload.eventType === 'INSERT') {
            const newContact = payload.new as AccountContact
            if (!contactMatchesScope(user, newContact)) {
              console.log('⏭️ [useInfiniteContacts] New contact out of scope, skipping')
              return
            }
            setContacts(prev => {
              const exists = prev.some(c => c.id === newContact.id)
              if (exists) return prev
              console.log('✅ [useInfiniteContacts] Adding new contact')
              return [newContact, ...prev]
            })
            setTotalCount(prev => prev + 1)
          } else if (payload.eventType === 'UPDATE') {
            const updatedContact = payload.new as AccountContact
            if (!contactMatchesScope(user, updatedContact)) {
              setContacts(prev => {
                if (!prev.some(c => c.id === updatedContact.id)) return prev
                console.log('🚫 [useInfiniteContacts] Contact out of scope, removing:', updatedContact.id)
                return prev.filter(c => c.id !== updatedContact.id)
              })
              setTotalCount(prev => Math.max(0, prev - 1))
              return
            }
            setContacts(prev => {
              console.log('🔄 [useInfiniteContacts] Updating contact:', updatedContact.id)
              const exists = prev.some(c => c.id === updatedContact.id)
              if (!exists) {
                setTotalCount(p => p + 1)
                return [updatedContact, ...prev]
              }
              return prev.map(c => c.id === updatedContact.id ? updatedContact : c)
            })
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as AccountContact).id
            setContacts(prev => {
              console.log('🗑️ [useInfiniteContacts] Removing contact:', deletedId)
              return prev.filter(c => c.id !== deletedId)
            })
            setTotalCount(prev => Math.max(0, prev - 1))
          }
        }
      )
      .subscribe((status) => {
        console.log('🔌 [useInfiniteContacts] Subscription status:', status)
      })

    return () => {
      console.log('🔌 [useInfiniteContacts] Unsubscribing from contacts')
      if (supabase) {
        supabase.removeChannel(channel)
      }
    }
  }, [user?.account_id, fetchContacts])

  return {
    contacts,
    initialLoading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
    updateContact,
    totalCount,
  }
}
